import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../OJTCoordinator/OJTCoordinator.css'; // Reuse the coordinator styling
import './SupervisorHome.css'; // Supervisor-specific styles
import { FaUser, FaUsers, FaComments, FaClipboardCheck } from 'react-icons/fa';
import { FaLocationDot, FaArrowRightFromBracket } from 'react-icons/fa6';
import { supabase } from '../../lib/supabaseClient';
import { useSupervisorAuth } from '../../lib/AuthProvider';

interface MyStudent {
  id: string;
  name: string;
  department: string;
  studentId: string;
  totalHours: number;
  progressRate: number;
  supervisor: string;
  trackingId: string;
  assignedDate: string;
}

const SupervisorHome: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [myStudents, setMyStudents] = useState<MyStudent[]>([]);
  const [supervisorOffice, setSupervisorOffice] = useState<string>('');

  // Use auth context to derive supervisor identity and loading state
  const { supervisor, loading: authLoading, signOut } = useSupervisorAuth();

  // When supervisor is available and not loading, fetch students based on office
  useEffect(() => {
    if (authLoading) return;
    if (supervisor?.office) {
      setSupervisorOffice(supervisor.office);
      fetchMyStudents(supervisor.office);
    }
  }, [authLoading, supervisor?.office]);

  // Fetch students where department matches supervisor's office
  const fetchMyStudents = async (supervisorOffice: string) => {
    try {
      // Get all students where department matches supervisor's office
      const { data: studentsData, error } = await supabase
        .from('student_profile')
        .select('*')
        .eq('department', supervisorOffice)
        .order('last_name', { ascending: true });

      if (error) {
        console.error('Error fetching students:', error);
        return;
      }

      // Fetch daily hours for each student
      const studentsWithHours = await Promise.all(
        (studentsData || []).map(async (student: any) => {
          const { data: dailyHours } = await supabase
            .from('daily_hours')
            .select('total_hours')
            .eq('user_id', student.id);

          const totalHours = dailyHours?.reduce((sum: number, day: any) => sum + day.total_hours, 0) || 0;
          const progressRate = Math.round((totalHours / 500) * 100);

          return {
            id: student.id,
            name: `${student.first_name} ${student.last_name}`,
            department: student.department,
            studentId: student.student_id,
            totalHours: Math.round(totalHours * 100) / 100,
            progressRate: Math.min(progressRate, 100),
            supervisor: student.supervisor || '',
            trackingId: '', // Not needed anymore
            assignedDate: student.created_at || new Date().toISOString()
          };
        })
      );

      setMyStudents(studentsWithHours);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="coordinator-home">
      <header className="top-nav">
        <div className="nav-container">
          <div className="logo">
            <h1>InternTr<FaLocationDot className="location-icon" />ckr</h1>
          </div>
          <button
            className="hamburger-btn"
            aria-label="Open menu"
            aria-controls="supervisor-sidebar"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(true)}
          >
            {'\u2630'}
          </button>
          <div className="nav-links">
            <button 
              onClick={() => navigate('/supervisor/home')} 
              className={`nav-link ${location.pathname === '/supervisor/home' ? 'active' : ''}`}
            >
              <FaUsers className="nav-icon" /> My Trainees
            </button>
            <button 
              onClick={() => navigate('/supervisor/attendance')} 
              className={`nav-link ${location.pathname === '/supervisor/attendance' ? 'active' : ''}`}
            >
              <FaClipboardCheck className="nav-icon" /> Attendance
            </button>
            <button 
              onClick={() => navigate('/supervisor/feedback')} 
              className={`nav-link ${location.pathname === '/supervisor/feedback' ? 'active' : ''}`}
            >
              <FaComments className="nav-icon" /> Feedback
            </button>
            <button 
              onClick={() => navigate('/supervisor/profile')} 
              className={`nav-link ${location.pathname === '/supervisor/profile' ? 'active' : ''}`}
            >
              <FaUser className="nav-icon" /> Profile
            </button>
          </div>
          <button onClick={handleSignOut} className="nav-link logout-button"><FaArrowRightFromBracket className="nav-icon" /> Sign Out</button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="supervisor-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={(e) => {
          // Only close menu if clicking on a link, not on the sign out button
          if ((e.target as HTMLElement).closest('.sidebar-logout-button')) {
            return;
          }
          setIsMenuOpen(false);
        }}>
          <button 
            onClick={() => navigate('/supervisor/home')} 
            className="nav-link"
          >
            <FaUsers className="nav-icon" /> My Trainees
          </button>
          <button 
            onClick={() => navigate('/supervisor/attendance')} 
            className="nav-link"
          >
            <FaClipboardCheck className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/supervisor/feedback')} 
            className="nav-link"
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/supervisor/profile')} 
            className="nav-link"
          >
            <FaUser className="nav-icon" /> Profile
          </button>
        </nav>
        <div className="sidebar-footer">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleSignOut();
            }} 
            className="sidebar-logout-button"
          >
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <div className="students-section">
          <div className="students-header">
            <h3 className="coordinator-title">My Trainees</h3>
            {supervisorOffice && (
              <p className="office-info">Showing trainees from: <strong>{supervisorOffice}</strong></p>
            )}
          </div>

          {myStudents.length > 0 ? (
            <div className="trainee-table general-view">
              <table>
                <thead>
                  <tr>
                    <th>Trainee</th>
                    <th>Department</th>
                    <th>Total Hours</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {myStudents.map((student, index) => (
                    <tr key={student.id}>
                      <td>{index + 1}.) {student.name}</td>
                      <td>{student.department}</td>
                      <td>
                        <div className="progress-container">
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${student.progressRate}%` }} 
                            />
                          </div>
                          <div className="progress-text">
                            <span>Progress Done: {student.progressRate}%</span>
                            <span>{student.totalHours}/500 hours</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="view-button">View</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data-message">
              <h3>No trainees found</h3>
              <p>There are currently no trainees in your department ({supervisorOffice}).</p>
              <p>Trainees will automatically appear here when their department matches your office.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SupervisorHome;